import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, BookOpen, Clock, Paperclip, Upload, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const TrainingBuilder = ({ content, onChange, onContentChange }) => {
    const [training, setTraining] = useState({
        sections: []
    });

    useEffect(() => {
        try {
            if (content) {
                // Handle both string (from database) and object (from AI generation)
                if (typeof content === 'string' && content.trim()) {
                    const parsed = JSON.parse(content);
                    setTraining(parsed);
                } else if (typeof content === 'object' && content !== null) {
                    // Content is already an object (from AI generation)
                    setTraining(content);
                }
            }
        } catch (e) {
            // If content is not valid JSON, initialize empty
            setTraining({ sections: [] });
        }
    }, [content]);

    const handleTrainingChange = (updatedTraining) => {
        setTraining(updatedTraining);
        onChange(JSON.stringify(updatedTraining, null, 2));
    };

    const addSection = () => {
        const newSection = {
            id: Date.now(),
            title: '',
            content: '',
            duration: 10, // minutes
            resources: [],
            attachments: []
        };

        handleTrainingChange({
            ...training,
            sections: [...training.sections, newSection]
        });
        onContentChange?.('training_sections', `Added new section ${training.sections.length + 1}`);
    };

    const updateSection = (index, field, value) => {
        const updatedSections = [...training.sections];
        updatedSections[index] = { ...updatedSections[index], [field]: value };

        handleTrainingChange({ ...training, sections: updatedSections });

        let valueDisplay = value;
        if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }
        onContentChange?.('training_sections', `Updated ${field} for section ${index + 1} to: "${valueDisplay}"`);
    };

    const deleteSection = (index) => {
        const updatedSections = training.sections.filter((_, idx) => idx !== index);
        handleTrainingChange({ ...training, sections: updatedSections });
        onContentChange?.('training_sections', `Deleted section ${index + 1}`);
    };

    const moveSection = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < training.sections.length) {
            const updatedSections = [...training.sections];
            [updatedSections[index], updatedSections[newIndex]] =
                [updatedSections[newIndex], updatedSections[index]];

            handleTrainingChange({ ...training, sections: updatedSections });
        }
    };

    const addResource = (sectionIndex) => {
        const updatedSections = [...training.sections];
        const section = { ...updatedSections[sectionIndex] };
        const resources = section.resources ? [...section.resources] : [];

        resources.push({ title: '', url: '' });
        section.resources = resources;
        updatedSections[sectionIndex] = section;

        handleTrainingChange({ ...training, sections: updatedSections });
    };

    const updateResource = (sectionIndex, resourceIndex, field, value) => {
        const updatedSections = [...training.sections];
        const section = { ...updatedSections[sectionIndex] };
        const resources = [...section.resources];

        resources[resourceIndex] = { ...resources[resourceIndex], [field]: value };
        section.resources = resources;
        updatedSections[sectionIndex] = section;

        handleTrainingChange({ ...training, sections: updatedSections });

        let valueDisplay = value;
        if (typeof value === 'string' && value.length > 50) {
            valueDisplay = value.substring(0, 50) + '...';
        }
        onContentChange?.('training_sections', `Updated resource ${field} for section ${sectionIndex + 1} to: "${valueDisplay}"`);
    };

    const removeResource = (sectionIndex, resourceIndex) => {
        const updatedSections = [...training.sections];
        const section = { ...updatedSections[sectionIndex] };
        section.resources = section.resources.filter((_, idx) => idx !== resourceIndex);
        updatedSections[sectionIndex] = section;

        handleTrainingChange({ ...training, sections: updatedSections });
    };

    // Section file attachment handlers
    const [uploadingSection, setUploadingSection] = useState(null);
    const fileInputRefs = useRef({});

    const handleSectionFileUpload = async (sectionIndex, files) => {
        if (!files || files.length === 0) return;

        // Validate file size (max 10MB each)
        const invalidFiles = Array.from(files).filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error('Some files exceed the 10 MB limit');
            return;
        }

        setUploadingSection(sectionIndex);

        try {
            const formData = new FormData();
            Array.from(files).forEach(file => {
                formData.append('files', file);
            });

            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                const updatedSections = [...training.sections];
                const section = { ...updatedSections[sectionIndex] };
                const attachments = section.attachments ? [...section.attachments] : [];

                attachments.push(...response.data.files);
                section.attachments = attachments;
                updatedSections[sectionIndex] = section;

                handleTrainingChange({ ...training, sections: updatedSections });
                toast.success(`${response.data.files.length} file(s) uploaded`);
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload file');
        } finally {
            setUploadingSection(null);
            if (fileInputRefs.current[sectionIndex]) {
                fileInputRefs.current[sectionIndex].value = '';
            }
        }
    };

    const removeSectionAttachment = (sectionIndex, attachmentIndex) => {
        const updatedSections = [...training.sections];
        const section = { ...updatedSections[sectionIndex] };
        section.attachments = section.attachments.filter((_, idx) => idx !== attachmentIndex);
        updatedSections[sectionIndex] = section;

        handleTrainingChange({ ...training, sections: updatedSections });
        toast.success('File removed');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Training Builder</h3>
                <button
                    type="button"
                    onClick={addSection}
                    className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Section
                </button>
            </div>

            {training.sections.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center">
                    <BookOpen className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-slate-300 mb-4">No training sections yet. Click "Add Section" to get started.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {training.sections.map((section, sIndex) => (
                        <div key={section.id || sIndex} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                            {/* Section Header */}
                            <div className="flex items-start gap-3 mb-4">
                                <div className="flex flex-col gap-1">
                                    <button
                                        type="button"
                                        onClick={() => moveSection(sIndex, 'up')}
                                        disabled={sIndex === 0}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveSection(sIndex, 'down')}
                                        disabled={sIndex === training.sections.length - 1}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-semibold text-gray-600 dark:text-slate-300">Section {sIndex + 1}</span>
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                                            <input
                                                type="number"
                                                value={section.duration || 10}
                                                onChange={(e) => updateSection(sIndex, 'duration', parseInt(e.target.value) || 10)}
                                                min="1"
                                                className="w-16 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-sm"
                                            />
                                            <span className="text-xs text-gray-500 dark:text-slate-400">min</span>
                                        </div>
                                    </div>

                                    <input
                                        type="text"
                                        value={section.title}
                                        onChange={(e) => updateSection(sIndex, 'title', e.target.value)}
                                        placeholder="Section Title"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-medium mb-3"
                                    />

                                    <textarea
                                        value={section.content}
                                        onChange={(e) => updateSection(sIndex, 'content', e.target.value)}
                                        placeholder="Enter training content for this section..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        rows="6"
                                    />
                                </div>

                                <button
                                    type="button"
                                    onClick={() => deleteSection(sIndex)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete Section"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Resources */}
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200">Additional Resources:</label>
                                    <button
                                        type="button"
                                        onClick={() => addResource(sIndex)}
                                        className="text-sm text-primary hover:text-primary-hover font-medium"
                                    >
                                        + Add Resource
                                    </button>
                                </div>

                                {section.resources && section.resources.length > 0 ? (
                                    <div className="space-y-2">
                                        {section.resources.map((resource, rIndex) => (
                                            <div key={rIndex} className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={resource.title}
                                                    onChange={(e) => updateResource(sIndex, rIndex, 'title', e.target.value)}
                                                    placeholder="Resource Title"
                                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                />
                                                <input
                                                    type="url"
                                                    value={resource.url}
                                                    onChange={(e) => updateResource(sIndex, rIndex, 'url', e.target.value)}
                                                    placeholder="https://..."
                                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeResource(sIndex, rIndex)}
                                                    className="p-2 text-red-600 hover:bg-red-50 dark:bg-red-500/10 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-slate-400 italic">No resources added yet</p>
                                )}
                            </div>

                            {/* Section Attachments */}
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-slate-200 flex items-center gap-1">
                                        <Paperclip className="w-4 h-4" />
                                        Section Attachments:
                                    </label>
                                </div>

                                {/* Upload Button */}
                                <div
                                    onClick={() => fileInputRefs.current[sIndex]?.click()}
                                    className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors mb-2 ${uploadingSection === sIndex
                                        ? 'border-primary/50 bg-primary/5'
                                        : 'border-gray-300 dark:border-slate-600 hover:border-primary/50 hover:bg-primary/5'
                                        }`}
                                >
                                    {uploadingSection === sIndex ? (
                                        <div className="flex items-center justify-center gap-2 text-primary">
                                            <Loader className="w-4 h-4 animate-spin" />
                                            <span className="text-sm">Uploading...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-slate-300">
                                            <Upload className="w-4 h-4" />
                                            <span className="text-sm">Click to upload files</span>
                                        </div>
                                    )}
                                    <input
                                        ref={el => fileInputRefs.current[sIndex] = el}
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm"
                                        onChange={(e) => handleSectionFileUpload(sIndex, e.target.files)}
                                        className="hidden"
                                        disabled={uploadingSection === sIndex}
                                    />
                                </div>

                                {/* Attached Files List */}
                                {section.attachments && section.attachments.length > 0 && (
                                    <div className="space-y-1">
                                        {section.attachments.map((file, aIndex) => (
                                            <div
                                                key={aIndex}
                                                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg text-sm"
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Paperclip className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                                    <span className="truncate text-gray-700 dark:text-slate-200">{file.file_name}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeSectionAttachment(sIndex, aIndex)}
                                                    className="p-1 text-red-500 hover:bg-red-50 dark:bg-red-500/10 rounded transition-colors flex-shrink-0"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Training Summary */}
            {training.sections.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-primary">
                            Total Sections: {training.sections.length}
                        </span>
                        <span className="font-medium text-primary flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Estimated Duration: {training.sections.reduce((sum, s) => sum + (s.duration || 0), 0)} minutes
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingBuilder;

